import { useRef, useState, type ReactNode } from 'react'
import Icon from '../../docs/Icon'
import Tabs from '../ui/Tabs'
import InPageNav from '../ui/InPageNav'
import DropdownMenu from '../ui/DropdownMenu'

// Live examples for tabs, in-page navigation, the dropdown menu and links. A
// mini specimen is a still preview for the catalog: no ids, nothing focusable.

type SpecimenProps = { mini?: boolean }

function Still({ children }: { children: ReactNode }) {
  return <div className="lib-framed lib-still">{children}</div>
}

function TabsSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span className="mds-tabs__list lib-fake-tabs">
          <span className="mds-tabs__tab is-selected">Overview</span>
          <span className="mds-tabs__tab">Documents</span>
          <span className="mds-tabs__tab">History</span>
        </span>
        <span className="mds-muted lib-fake-panel">Submitted October 2, 2026. In review.</span>
      </Still>
    )
  return (
    <Tabs
      label="Application details"
      tabs={[
        {
          id: 'overview',
          label: 'Overview',
          content: <p>Submitted October 2, 2026. Your application is in review, and most reviews finish within three weeks.</p>,
        },
        {
          id: 'documents',
          label: 'Documents',
          content: (
            <ul className="mds-prose">
              <li>Proof of identity, received</li>
              <li>Proof of address, received</li>
              <li>Signed form, waiting</li>
            </ul>
          ),
        },
        {
          id: 'history',
          label: 'History',
          content: <p>October 2: submitted online. October 4: documents received.</p>,
        },
      ]}
    />
  )
}

const sections = [
  ['spec-inpage-eligibility', 'Eligibility', 'You can apply if you have lived in the state for at least a year.'],
  ['spec-inpage-documents', 'Documents you need', 'Bring proof of identity, proof of address and the signed form.'],
  ['spec-inpage-apply', 'How to apply', 'Apply online, by mail or in person at a field office.'],
  ['spec-inpage-after', 'After you apply', 'You will get an email at each step, and a decision within three weeks.'],
]

function InPageNavigationSpecimen({ mini }: SpecimenProps) {
  const content = useRef<HTMLDivElement>(null)
  if (mini)
    return (
      <Still>
        <span className="mds-in-page-nav__title">On this page</span>
        <span className="mds-in-page-nav__list">
          <span className="mds-in-page-nav__link">Eligibility</span>
          <span className="mds-in-page-nav__link" aria-current="location">
            Documents you need
          </span>
          <span className="mds-in-page-nav__link">How to apply</span>
        </span>
      </Still>
    )
  return (
    <div className="lib-in-page-demo">
      <InPageNav container={content} />
      <div ref={content} className="lib-in-page-demo__content">
        {sections.map(([id, heading, text]) => (
          <section key={id} className="lib-in-page-demo__section">
            <h2 id={id} className="mds-h3">
              {heading}
            </h2>
            <p>{text}</p>
          </section>
        ))}
      </div>
    </div>
  )
}

function DropdownMenuSpecimen({ mini }: SpecimenProps) {
  const [last, setLast] = useState('Nothing chosen yet.')
  if (mini)
    return (
      <Still>
        <span className="mds-button mds-button--secondary mds-button--sm lib-fake-dropdown">
          Actions <Icon name="chevron" size={14} className="mds-dropdown__chevron" />
        </span>
        <span className="lib-fake-list">
          <span className="is-active">Download a copy</span>
          <span>Share</span>
          <span>Withdraw</span>
        </span>
      </Still>
    )
  const items = ['Download a copy', 'Share', 'Print', 'Withdraw'].map((label) => ({
    label,
    onSelect: () => setLast(`Chose ${label.toLowerCase()}.`),
  }))
  return (
    <div className="lib-stack">
      <DropdownMenu label="Actions" items={items} />
      <p className="mds-muted" aria-live="polite">
        {last}
      </p>
    </div>
  )
}

function LinkSpecimen({ mini }: SpecimenProps) {
  if (mini)
    return (
      <Still>
        <span>
          Read <span className="mds-link">who can apply</span> or visit{' '}
          <span className="mds-link mds-link--external">
            USA.gov
            <Icon name="external" size={12} />
          </span>
        </span>
      </Still>
    )
  return (
    <p>
      Read <a className="mds-link" href="#spec-link-eligibility">who can apply</a> before you start, then find your state office on{' '}
      <a className="mds-link mds-link--external" href="https://www.usa.gov/states-and-territories" target="_blank" rel="noopener noreferrer">
        USA.gov
        <span className="mds-visually-hidden"> (opens in a new tab)</span>
        <Icon name="external" size={12} />
      </a>
      .
    </p>
  )
}

export const navigationSpecimens: Record<string, (props: { mini?: boolean }) => ReactNode> = {
  tabs: TabsSpecimen,
  'in-page-navigation': InPageNavigationSpecimen,
  'dropdown-menu': DropdownMenuSpecimen,
  link: LinkSpecimen,
}
